import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import SEO from "../components/SEO";

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState(variant === "cancel" ? "cancel" : "loading");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (variant === "cancel" || !session_id) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${session_id}`);
        if (!alive) return;
        if (r.data.payment_status === "paid") setState("success");
        else if (["failed", "expired"].includes(r.data.payment_status)) setState("failed");
        else if (tries < 12) { setTries((t) => t + 1); setTimeout(poll, 2000); }
        else setState("timeout");
      } catch { setState("failed"); }
    };
    poll();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [session_id]);

  const cfg = {
    loading: { icon: Loader2, iconClass: "animate-spin text-nexus-blue", title: "Confirming your payment…", msg: "One sec — Stripe is finalising things." },
    success: { icon: CheckCircle2, iconClass: "text-emerald-400", title: "You're in!", msg: "Your subscription is active. Enjoy unlimited campaigns." },
    cancel: { icon: XCircle, iconClass: "text-zinc-400", title: "Payment cancelled", msg: "No charge was made. You can pick a plan any time." },
    failed: { icon: XCircle, iconClass: "text-nexus-pink", title: "Something went wrong", msg: "Payment didn't complete. Try again or reach out to support." },
    timeout: { icon: Loader2, iconClass: "text-zinc-400", title: "Still confirming", msg: "It's taking longer than usual — check your billing page in a minute." },
  }[state];
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex items-center justify-center px-6">
      <SEO title={`${cfg.title} · Nexus`} description={cfg.msg} path="/payment/success" />
      <div className="glass rounded-3xl p-10 max-w-md text-center" data-testid="payment-result">
        <Icon className={`h-14 w-14 mx-auto ${cfg.iconClass}`} />
        <h1 className="font-display font-bold text-3xl tracking-tighter mt-6" data-testid="payment-title">{cfg.title}</h1>
        <p className="text-sm text-zinc-400 mt-2">{cfg.msg}</p>
        <div className="mt-6 flex gap-2 justify-center">
          <Link to="/brand/billing" className="px-5 py-2 rounded-full bg-white/5 border border-white/15 text-sm text-zinc-200 hover:bg-white/10 hover:text-white transition-colors" data-testid="payment-back-billing">Back to Billing</Link>
          <Link to="/brand" className="px-5 py-2 rounded-full nexus-gradient text-white font-semibold text-sm hover:opacity-90" data-testid="payment-back-dashboard">Go to Dashboard</Link>
        </div>
      </div>
    </div>
  );
}
